<template>
    <div class="w-full h-full">
        <base-form class="h-full w-full">
            <template #body>
                <div class="grid grid-cols-4 w-full gap-2 mb-1 p-1 text-xs">
                    <div class="flex flex-col">
                        <label>Campaign Name</label>
                        <input type="text" required class="p-1 border w-full inline" v-model="editedCampaign.campaign_name">
                    </div>
                    <div class="flex flex-col">
                        <label>Workflow Status</label>
                        <select class="border p-1 w-full" v-model="editedCampaign.workflow_status">
                            <option required v-for="(workflowStatus, index) in workflowStatusOptions" :key="index"
                                :value="workflowStatus.value">
                                {{ workflowStatus.label }}
                            </option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label>Has Activities</label>
                        <select class="border p-1 w-full" v-model="editedCampaign.has_activities">
                            <option v-for="(activitiesStatus, index) in activitiesStatusOptions" :key="index"
                                :value="activitiesStatus.value">
                                {{ activitiesStatus.label }}
                            </option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label>Client</label>
                        <select class="border p-1 w-full" v-model="editedCampaign.client_id">
                            <option v-for="(client, index) in clientsList" :key="index" :value="client.id">
                                {{ client.client_name }}
                            </option>
                        </select>
                    </div>
                    <div v-if="isOwner" class="col-span-4 grid grid-cols-5 gap-1 w-full my-2">
                    <div class="col-span-5 w-full border-b">
                        Campaign Owners
                    </div>
                    <div class="flex w-full col-span-2 h-full justify-center">
                        <div class="flex flex-col h-full border w-full">
                        <div class="w-full bg-gray-200 p-2 flex items-center justify-center">
                            Available Users
                        </div>
                        <div class="flex flex-wrap p-2 max-h-44 overflow-auto">
                            <div v-for="user in potentialOwners" :key="user.id" :draggable="true" :ondragstart="dragStartHandler" :ondragend="dragEndHandler" :id="user.id" class="m-1 py-1 px-2 w-fit flex items-center h-fit  rounded-full bg-blue-100 hover:cursor-grab">
                                {{ user.first_name }} {{ user.last_name }}
                            </div>
                        </div>
                        </div>
                    </div>
                    <div class="flex w-full h-full justify-center">
                        <div class="flex flex-col h-full items-center justify-center w-full">
                            <div class="flex flex-col">
                                <div class="h-fit p-1 flex items-center rounded-md bg-green-200">
                                    Drag Available Users into the Owners column to assign ownership
                                </div>
                                <div class="h-1/2 w-full flex justify-center items-center">
                                    <RightArrowSVG 
                                        class="h-6 w-12"
                                        stroke-color="black"
                                        stroke-width="1.5"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex col-span-2 w-full h-full justify-center">
                        <div class="flex flex-col h-full border w-full">
                        <div class="w-full bg-gray-200 p-2 flex items-center justify-center">
                            Owners
                        </div>
                        <div class="flex flex-wrap p-2 max-h-44 overflow-auto w-full" :ondragover="dragOverHandler" :ondrop="dropHandler">
                            <div v-for="user in editedCampaign.campaign_owners" :key="user.id" class="m-1 py-1 px-2 w-max rounded-full bg-blue-100 flex items-center justify-between">
                            <div class="mr-2 whitespace-nowrap">
                                {{ user.first_name }} {{ user.last_name }}
                            </div>
                            <div>
                                <base-button-negative @click.prevent="removeCampaignOwner(user.id)">
                                <XMarkSVG icon-class="w-2 h-2" strokeColor="white" />
                                </base-button-negative>
                            </div>
                            </div>
                        </div>
                        </div>
                    </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex flex-row space-x-2 justify-center w-full col-span-4 my-2">
                    <base-button-affirmative @click="editCampaign(editedCampaign)">Save</base-button-affirmative>
                    <base-button-negative @click="closeEdit">Cancel</base-button-negative>
                </div>
            </template>
        </base-form>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, reactive, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { getAllClientsSimple } from "@/services/Client"
import { updateCampaign } from '@/services/Campaigns.js';
import { useToast } from 'vue-toast-notification';
import XMarkSVG from '../svg-components/XMarkSVG.vue';
import RightArrowSVG from '../svg-components/RightArrowSVG.vue';

const store = useStore()
const $toast = useToast()
const props = defineProps(['campaignToEdit', 'users'])

const emit = defineEmits(['close-edit', 'refetch-campaigns'])

function closeEdit() {
    emit('close-edit')
}

const editedCampaign = reactive({
    campaign_name: props.campaignToEdit.campaign_name,
    status: props.campaignToEdit.status,
    workflow_status: props.campaignToEdit.workflow_status,
    has_activities: props.campaignToEdit.has_activities == null ? null : props.campaignToEdit.has_activities,
    client_id: props.campaignToEdit.client == null ? null : props.campaignToEdit.client.id,
    campaign_owners: props.campaignToEdit.owners
})

const activitiesStatusOptions = ref([
    { value: null, label: "Unknown" },
    { value: true, label: "Yes" },
    { value: false, label: "No" },
]);


const potentialOwners = computed(() => {
  const owners = editedCampaign.campaign_owners.map((user) => user.id)
  return props.users.filter((user) => {
    return !owners.includes(user.id)
  })
})

const isOwner = ref(props.campaignToEdit.owners.map((user) => user.id).includes(store.state.auth.user.id))

function removeCampaignOwner(userId){
  editedCampaign.campaign_owners.splice(editedCampaign.campaign_owners.findIndex((user) => user.id == userId), 1)
}

const isDragging = ref(false)
const draggingUser = ref(null)

function dragOverHandler(ev) {
  ev.preventDefault();
}

function dropHandler(ev) {
  ev.preventDefault();
  isDragging.value = false
  editedCampaign.campaign_owners.push(draggingUser.value)
}

function dragStartHandler(ev) {
  isDragging.value = true
  ev.dataTransfer.effectAllowed = "copyMove";
  draggingUser.value = potentialOwners.value.find((user) => {
    return user.id == ev.target.id
  })
}

function dragEndHandler(ev) {
    ev.preventDefault()
    isDragging.value = false
    draggingUser.value = null
}

const workflowStatusOptions = ref([
    { value: "None", label: "None" },
    { value: "Active Outreach", label: "Active Outreach" },
    { value: "Client Review", label: "Client Review" },
    { value: "On Hold", label: "On Hold" },
    { value: "Prospect", label: "Prospect" },
    { value: "Research", label: "Research" },
    { value: "Retired", label: "Retired" },
    { value: "Vetting Targets", label: "Vetting Targets" },
])

const clientsList = ref([])

async function loadClients() {
    const result = await getAllClientsSimple({ token: store.state.auth.token });
    clientsList.value = result.data;
    const removeClientOption = {client_id: "clear", client_name: "------------"}
    clientsList.value.unshift(removeClientOption)
    // console.log(clientsList.value)
}

async function editCampaign() {
    const token = store.state.auth.token
    let errorMessage = "Error: Missing "
    if (!editedCampaign.campaign_name || !editedCampaign.workflow_status) {
        if (!editedCampaign.campaign_name) {
            errorMessage += "campaign name, "
        }
        if (!editedCampaign.workflow_status) {
            errorMessage += "workflow status, "
        }
        alert(errorMessage.slice(0, -2))
        return
    }
    const result = await updateCampaign(props.campaignToEdit.id, editedCampaign, token)
    if(result.status == 200){
        $toast.open({
            message: `Successfully edited ${editedCampaign.campaign_name}`,
            type: 'success',
            duration: 3000
        })
    }
    emit('refetch-campaigns')
    emit('close-edit')

}

onMounted(() => {
    loadClients();
});
</script>